import React, { useState, useEffect } from "react";
import AdminService from "../services/AdminService";
import { toast } from "react-toastify";
import IconPickerModal from "../components/IconPickerModal";

const PREDEFINED_ICONS = [
    { name: "Furniture", url: "https://cdn-icons-png.flaticon.com/128/5564/5564849.png" },
    { name: "Sofa", url: "https://cdn-icons-png.flaticon.com/128/2253/2253673.png" },
    { name: "Table", url: "https://cdn-icons-png.flaticon.com/128/2653/2653243.png" },
    { name: "Car & Bikes", url: "https://cdn-icons-png.flaticon.com/128/741/741407.png" },
    { name: "Motorcycle", url: "https://cdn-icons-png.flaticon.com/128/1986/1986937.png" },
    { name: "Mobiles", url: "https://cdn-icons-png.flaticon.com/128/644/644458.png" },
    { name: "Laptop", url: "https://cdn-icons-png.flaticon.com/128/428/428001.png" },
    { name: "Real Estate", url: "https://cdn-icons-png.flaticon.com/128/609/609803.png" },
    { name: "Home", url: "https://cdn-icons-png.flaticon.com/128/619/619034.png" },
    { name: "Apartment", url: "https://cdn-icons-png.flaticon.com/128/1904/1904550.png" },
    { name: "Jobs", url: "https://cdn-icons-png.flaticon.com/128/3850/3850285.png" },
    { name: "Briefcase", url: "https://cdn-icons-png.flaticon.com/128/2065/2065203.png" },
    { name: "Electronics", url: "https://cdn-icons-png.flaticon.com/128/3659/3659899.png" },
    { name: "Camera", url: "https://cdn-icons-png.flaticon.com/128/685/685655.png" },
    { name: "TV", url: "https://cdn-icons-png.flaticon.com/128/716/716429.png" },
    { name: "Pets", url: "https://cdn-icons-png.flaticon.com/128/616/616408.png" },
    { name: "Dog", url: "https://cdn-icons-png.flaticon.com/128/616/616554.png" },
    { name: "Cat", url: "https://cdn-icons-png.flaticon.com/128/616/616430.png" },
    { name: "Fashion", url: "https://cdn-icons-png.flaticon.com/128/3050/3050215.png" },
    { name: "Dress", url: "https://cdn-icons-png.flaticon.com/128/2422/2422616.png" },
    { name: "Shirt", url: "https://cdn-icons-png.flaticon.com/128/2503/2503380.png" },
    { name: "Services", url: "https://cdn-icons-png.flaticon.com/128/995/995260.png" },
    { name: "Cleaning", url: "https://cdn-icons-png.flaticon.com/128/2921/2921223.png" },
    { name: "Education", url: "https://cdn-icons-png.flaticon.com/128/2436/2436702.png" },
    { name: "Books", url: "https://cdn-icons-png.flaticon.com/128/2702/2702069.png" },
    { name: "Sports", url: "https://cdn-icons-png.flaticon.com/128/2312/2312431.png" },
    { name: "Gym & Fitness", url: "https://cdn-icons-png.flaticon.com/128/1614/1614390.png" },
    { name: "Soccer", url: "https://cdn-icons-png.flaticon.com/128/888/888871.png" },
    { name: "Health", url: "https://cdn-icons-png.flaticon.com/128/2966/2966327.png" },
    { name: "Medical", url: "https://cdn-icons-png.flaticon.com/128/687/687529.png" },
    { name: "Tree", url: "https://cdn-icons-png.flaticon.com/128/489/489969.png" },
    { name: "Garden", url: "https://cdn-icons-png.flaticon.com/128/1518/1518863.png" }
];

const CategoryManagement = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedCategories, setExpandedCategories] = useState([]);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [parentCategory, setParentCategory] = useState(null);
    const [showIconPicker, setShowIconPicker] = useState(false);
    const [categoryForm, setCategoryForm] = useState({
        name: "",
        slug: "",
        icon: "",
        parentCategoryId: 0,
        priceRequired: false,
        freeAdPrice: 0
    });
    const [subCategoryForm, setSubCategoryForm] = useState({
        name: "",
        slug: "",
        icon: "",
        parentCategoryId: 0,
        priceRequired: false,
        freeAdPrice: 0
    });

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            const response = await AdminService.getCategories();
            setCategories(response.data.data || []);
        } catch (error) {
            console.error("Error loading categories:", error);
            toast.error("Failed to load categories");
            setCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const toggleExpand = (categoryId) => {
        setExpandedCategories(prev =>
            prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        );
    };

    const handleSaveCategory = async () => {
        if (!categoryForm.name.trim()) {
            toast.error("Category name is required");
            return;
        }
        try {
            const payload = {
                ...categoryForm,
                parentCategoryId: categoryForm.parentCategoryId || 0
            };

            if (editingCategory) {
                const response = await AdminService.updateCategory(editingCategory.id, payload);
                if (response.data.success) {
                    toast.success("Category updated successfully");
                    loadCategories();
                } else {
                    toast.error(response.data.message || "Failed to update category");
                }
            } else {
                const response = await AdminService.saveCategory(payload);
                if (response.data.success) {
                    toast.success("Category created successfully");
                    loadCategories();
                } else {
                    toast.error(response.data.message || "Failed to create category");
                }
            }
        } catch (error) {
            console.error("Error saving category:", error);
            const errorMsg = error.response?.data?.message || "Failed to save category";
            toast.error(errorMsg);
        }
        setShowCategoryModal(false);
        setCategoryForm({
            name: "",
            slug: "",
            icon: "",
            parentCategoryId: 0,
            priceRequired: false,
            freeAdPrice: 0
        });
        setEditingCategory(null);
    };

    const handleDeleteCategory = async (category) => {
        if (!window.confirm(`Are you sure you want to delete category "${category.name}"?`)) return;
        try {
            const response = await AdminService.deleteCategory(category.id);
            if (response.data.success) {
                toast.success("Category deleted successfully");
                loadCategories();
            } else {
                toast.error(response.data.message || "Failed to delete category");
            }
        } catch (error) {
            console.error("Error deleting category:", error);
            const errorMsg = error.response?.data?.message || "Failed to delete category";
            toast.error(errorMsg);
        }
    };

    const handleAddSubCategory = async () => {
        if (!subCategoryForm.name.trim()) {
            toast.error("Sub-category name is required");
            return;
        }
        try {
            const payload = {
                ...subCategoryForm,
                parentCategoryId: parentCategory.id
            };
            const response = await AdminService.saveCategory(payload);
            if (response.data.success) {
                toast.success("Sub-category added successfully");
                loadCategories();
            } else {
                toast.error(response.data.message || "Failed to add sub-category");
            }
        } catch (error) {
            console.error("Error adding sub-category:", error);
            const errorMsg = error.response?.data?.message || "Failed to add sub-category";
            toast.error(errorMsg);
        }
        setShowSubCategoryModal(false);
        setSubCategoryForm({
            name: "",
            slug: "",
            icon: "",
            parentCategoryId: 0,
            priceRequired: false,
            freeAdPrice: 0
        });
        setParentCategory(null);
    };

    const handleDeleteSubCategory = async (category, subCategory) => {
        if (!window.confirm(`Are you sure you want to delete sub-category "${subCategory.name}"?`)) return;
        try {
            const response = await AdminService.deleteSubCategory(category.id, subCategory.id);
            if (response.data.success) {
                toast.success("Sub-category deleted successfully");
                loadCategories();
            } else {
                toast.error(response.data.message || "Failed to delete sub-category");
            }
        } catch (error) {
            console.error("Error deleting sub-category:", error);
            const errorMsg = error.response?.data?.message || "Failed to delete sub-category";
            toast.error(errorMsg);
        }
    };

    const openEditModal = (category) => {
        setEditingCategory(category);
        setCategoryForm({
            name: category.name,
            slug: category.slug || "",
            icon: category.icon || "",
            parentCategoryId: 0,
            priceRequired: category.priceRequired || false,
            freeAdPrice: category.freeAdPrice || 0
        });
        setShowCategoryModal(true);
    };

    if (loading) {
        return (
            <div className="admin-empty-state">
                <i className="fa fa-spinner fa-spin"></i>
                <p>Loading categories...</p>
            </div>
        );
    }

    return (
        <div>
            <div className="admin-card">
                <div className="admin-card-header">
                    <h3>Platform Categories</h3>
                    <button
                        className="admin-btn admin-btn-primary admin-btn-sm"
                        onClick={() => {
                            setEditingCategory(null);
                            setCategoryForm({
                                name: "",
                                slug: "",
                                icon: "",
                                parentCategoryId: 0,
                                priceRequired: false,
                                freeAdPrice: 0
                            });
                            setShowCategoryModal(true);
                        }}
                    >
                        <i className="fa fa-plus"></i> Add Category
                    </button>
                </div>
                <div className="admin-card-body" style={{ padding: 0 }}>
                    {categories.map((category) => (
                        <div key={category.id}>
                            <div className="admin-category-item">
                                <div className="admin-category-item-left">
                                    <button
                                        className="admin-btn-icon"
                                        onClick={() => toggleExpand(category.id)}
                                        style={{ fontSize: "12px" }}
                                    >
                                        <i className={`fa ${expandedCategories.includes(category.id) ? "fa-chevron-down" : "fa-chevron-right"}`}></i>
                                    </button>
                                    {category.icon && (category.icon.startsWith('http') || category.icon.includes('.')) ? (
                                        <div style={{ width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "10px" }}>
                                            <img
                                                src={category.icon.startsWith('http') ? category.icon : `https://api.caribbargains.com/images/${category.icon}`}
                                                alt=""
                                                style={{ maxWidth: "40px", maxHeight: "40px", borderRadius: "4px", objectFit: "contain" }}
                                                onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                                            />
                                        </div>
                                    ) : (
                                        <i className={`fa ${category.icon || "fa-folder"}`} style={{ fontSize: "24px", color: "#2a89ba", width: "40px", textAlign: 'center' }}></i>
                                    )}
                                    <div>
                                        <div style={{ fontWeight: 500, fontSize: "14px" }}>{category.name}</div>
                                        <div style={{ fontSize: "12px", color: "#6c757d" }}>
                                            {category.slug} • {category.subCategory?.length || 0} sub-categories
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        className="admin-btn-icon"
                                        title="Add Sub-category"
                                        onClick={() => { setParentCategory(category); setShowSubCategoryModal(true); }}
                                    >
                                        <i className="fa fa-plus-circle"></i>
                                    </button>
                                    <button className="admin-btn-icon" title="Edit" onClick={() => openEditModal(category)}>
                                        <i className="fa fa-pencil"></i>
                                    </button>
                                    <button className="admin-btn-icon" title="Delete" onClick={() => handleDeleteCategory(category)} style={{ color: "#dc3545" }}>
                                        <i className="fa fa-trash"></i>
                                    </button>
                                </div>
                            </div>

                            {/* Sub-categories */}
                            {expandedCategories.includes(category.id) && category.subCategory?.map((sub) => (
                                <div key={sub.id} className="admin-category-item admin-subcategory">
                                    <div className="admin-category-item-left">
                                        <div style={{ width: "40px", display: "flex", justifyContent: "center", marginRight: "10px" }}>
                                            <i className="fa fa-circle" style={{ fontSize: "6px", color: "#6c757d" }}></i>
                                        </div>
                                        <span style={{ fontSize: "14px" }}>{sub.name}</span>
                                    </div>
                                    <div>
                                        <button
                                            className="admin-btn-icon"
                                            title="Delete"
                                            onClick={() => handleDeleteSubCategory(category, sub)}
                                            style={{ color: "#dc3545" }}
                                        >
                                            <i className="fa fa-times"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {/* Add/Edit Category Modal */}
            {showCategoryModal && (
                <div className="admin-modal-overlay" onClick={() => setShowCategoryModal(false)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h3>{editingCategory ? "Edit Category" : "Add Category"}</h3>
                            <button className="admin-modal-close" onClick={() => setShowCategoryModal(false)}>×</button>
                        </div>
                        <div className="admin-modal-body">
                            <div className="admin-form-group">
                                <label>Category Name *</label>
                                <input
                                    type="text"
                                    className="admin-form-control"
                                    placeholder="e.g. Sports & Outdoors"
                                    value={categoryForm.name}
                                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Slug *</label>
                                <input
                                    type="text"
                                    className="admin-form-control"
                                    placeholder="e.g. sports-outdoors"
                                    value={categoryForm.slug}
                                    onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })}
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Category Icon</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    {categoryForm.icon ? (
                                        <div style={{ width: '50px', height: '50px', border: '1px solid var(--admin-border)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa' }}>
                                            <img src={categoryForm.icon} alt="Selected" style={{ maxWidth: '40px', maxHeight: '40px', objectFit: 'contain' }} />
                                        </div>
                                    ) : (
                                        <div style={{ width: '50px', height: '50px', border: '1px dashed var(--admin-border)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}>
                                            <i className="fa fa-image"></i>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="admin-btn admin-btn-outline admin-btn-sm"
                                        onClick={() => setShowIconPicker(true)}
                                    >
                                        Change Icon
                                    </button>
                                </div>
                            </div>
                            <div className="admin-form-group-flex" style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '15px' }}>
                                <div className="admin-form-check">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={categoryForm.priceRequired}
                                            onChange={(e) => setCategoryForm({ ...categoryForm, priceRequired: e.target.checked })}
                                        />
                                        Price Required
                                    </label>
                                </div>
                                <div className="admin-form-group" style={{ flex: 1, marginBottom: 0 }}>
                                    <label>Free Ad Price Limit</label>
                                    <input
                                        type="number"
                                        className="admin-form-control"
                                        value={categoryForm.freeAdPrice}
                                        onChange={(e) => setCategoryForm({ ...categoryForm, freeAdPrice: Number(e.target.value) })}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-btn admin-btn-outline" onClick={() => setShowCategoryModal(false)}>Cancel</button>
                            <button className="admin-btn admin-btn-primary" onClick={handleSaveCategory}>
                                {editingCategory ? "Save Changes" : "Create Category"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Sub-category Modal */}
            {showSubCategoryModal && parentCategory && (
                <div className="admin-modal-overlay" onClick={() => setShowSubCategoryModal(false)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h3>Add Sub-Category to "{parentCategory.name}"</h3>
                            <button className="admin-modal-close" onClick={() => setShowSubCategoryModal(false)}>×</button>
                        </div>
                        <div className="admin-modal-body">
                            <div className="admin-form-group">
                                <label>Sub-category Name *</label>
                                <input
                                    type="text"
                                    className="admin-form-control"
                                    placeholder="e.g. Basketball"
                                    value={subCategoryForm.name}
                                    onChange={(e) => setSubCategoryForm({ ...subCategoryForm, name: e.target.value })}
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Slug *</label>
                                <input
                                    type="text"
                                    className="admin-form-control"
                                    placeholder="e.g. basketball"
                                    value={subCategoryForm.slug}
                                    onChange={(e) => setSubCategoryForm({ ...subCategoryForm, slug: e.target.value })}
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Sub-category Icon</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    {subCategoryForm.icon ? (
                                        <div style={{ width: '50px', height: '50px', border: '1px solid var(--admin-border)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa' }}>
                                            <img src={subCategoryForm.icon} alt="Selected" style={{ maxWidth: '40px', maxHeight: '40px', objectFit: 'contain' }} />
                                        </div>
                                    ) : (
                                        <div style={{ width: '50px', height: '50px', border: '1px dashed var(--admin-border)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}>
                                            <i className="fa fa-image"></i>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="admin-btn admin-btn-outline admin-btn-sm"
                                        onClick={() => setShowIconPicker(true)}
                                    >
                                        Change Icon
                                    </button>
                                </div>
                            </div>
                            <div className="admin-form-group-flex" style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '15px' }}>
                                <div className="admin-form-check">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={subCategoryForm.priceRequired}
                                            onChange={(e) => setSubCategoryForm({ ...subCategoryForm, priceRequired: e.target.checked })}
                                        />
                                        Price Required
                                    </label>
                                </div>
                                <div className="admin-form-group" style={{ flex: 1, marginBottom: 0 }}>
                                    <label>Free Ad Price Limit</label>
                                    <input
                                        type="number"
                                        className="admin-form-control"
                                        value={subCategoryForm.freeAdPrice}
                                        onChange={(e) => setSubCategoryForm({ ...subCategoryForm, freeAdPrice: Number(e.target.value) })}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-btn admin-btn-outline" onClick={() => setShowSubCategoryModal(false)}>Cancel</button>
                            <button className="admin-btn admin-btn-primary" onClick={handleAddSubCategory}>Add Sub-Category</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Icon Picker Modal */}
            <IconPickerModal
                show={showIconPicker}
                onClose={() => setShowIconPicker(false)}
                icons={PREDEFINED_ICONS}
                initialIcon={showCategoryModal ? categoryForm.icon : subCategoryForm.icon}
                onSelect={(url) => {
                    if (showCategoryModal) {
                        setCategoryForm({ ...categoryForm, icon: url });
                    } else if (showSubCategoryModal) {
                        setSubCategoryForm({ ...subCategoryForm, icon: url });
                    }
                }}
            />
        </div>
    );
};

export default CategoryManagement;
